/**
 * Reglas de acceso por plan de suscripción.
 *
 * Antes de esto, cada página resolvía el acceso por su cuenta en su `beforeMount`,
 * comparando contra una cadena literal:
 *
 *     let Plan = Suscripcion.plan
 *     if (Plan == "Bronce") this.$router.push("/")     // 15 páginas
 *     if (Plan != "Oro")    this.$router.push("/")     //  4 páginas
 *
 * Eso trajo dos problemas. El `!= "Oro"` dejaba fuera a Platino, Diamante y
 * DiamanteElite, que pagan más que Oro — clientes de los planes más caros no
 * podían abrir NOM-035, Gestiones, Reglas de Vacaciones ni Periodos de Nóminas.
 * Y como la comparación estaba copiada en 19 archivos, agregar un plan obligaba
 * a revisarlos uno por uno.
 *
 * Aquí los planes de la escalera comercial se comparan por nivel, no por nombre,
 * así que "de Oro para arriba" no puede volver a escribirse como "igual a Oro".
 *
 * El backend tiene la definición equivalente en
 * V6.Api.eClock/Controllers/UsuariosController.cs (MenuFiltraPorPlan) del repo
 * eClock6. Las dos listas tienen que moverse juntas hasta que exista la tabla de
 * entitlements; ver Docs/NOM035.Entitlements.Analisis.md.
 */

/** Planes tal como los serializa la API (enum ePlan de eClock6). */
export const PLANES = {
  Desconocido: "Desconocido",
  Pruebas: "Pruebas",
  Gratuito: "Gratuito",
  Bronce: "Bronce",
  Plata: "Plata",
  Oro: "Oro",
  Platino: "Platino",
  Diamante: "Diamante",
  DiamanteElite: "DiamanteElite",
  NOM035: "NOM035",
};

/**
 * Escalera comercial: cada plan incluye lo del anterior. Sólo los planes de esta
 * tabla se pueden comparar por nivel.
 *
 * `Pruebas` comparte nivel con `Oro`: mismos privilegios, plan distinto. Se le da
 * su propio renglón en vez de tratarlo como alias para que siga siendo
 * identificable (etiqueta, badge, reportes) sin perder acceso. Ojo: hoy el
 * backend reescribe el plan a Oro al dar de alta la suscripción y sólo deja la
 * bandera `Suscripcion.Pruebas`, así que en la práctica `getPlan()` casi nunca
 * devuelve "Pruebas" — ver el TODO en SuscripcionesController.cs del repo
 * eClock6.
 *
 * `Platino`, `Diamante` y `DiamanteElite` están descontinuados comercialmente y
 * en el código nunca tuvieron privilegios distintos a los de Oro: los cuatro
 * caen en el mismo grupo tanto en MenuFiltraPorPlan como en los cuatro switches
 * de suscripciones.usuarios.service.js. Se conservan porque puede haber
 * suscripciones viejas guardadas con esos valores.
 */
const NIVELES = {
  [PLANES.Gratuito]: 0,
  [PLANES.Bronce]: 1,
  [PLANES.Plata]: 2,
  [PLANES.Oro]: 3,
  [PLANES.Pruebas]: 3,
  [PLANES.Platino]: 3,
  [PLANES.Diamante]: 3,
  [PLANES.DiamanteElite]: 3,
};

/**
 * Planes acotados a un conjunto de módulos. No están en la escalera: no son un
 * plan "más chico", son otro producto. Nunca pasan una regla por nivel; sólo
 * entran donde se les nombra explícitamente en `incluye`.
 */
const PLANES_ACOTADOS = [PLANES.NOM035];

/**
 * @param {string} plan
 * @returns {boolean} true si el plan es acotado a módulos (hoy sólo NOM035).
 */
export function EsPlanAcotado(plan) {
  return PLANES_ACOTADOS.indexOf(plan) >= 0;
}

/**
 * Resuelve si un plan cumple una regla de acceso.
 *
 * @param {string} plan Plan de la suscripción, tal como lo devuelve la API.
 * @param {Object} regla Regla de la ruta (`meta.plan`).
 * @param {string} [regla.minimo] Plan mínimo de la escalera comercial.
 * @param {string[]} [regla.incluye] Planes que entran aunque no cumplan `minimo`.
 * @returns {boolean}
 */
export function PuedeAccederPlan(plan, regla) {
  // Sin regla, la ruta no está restringida por plan.
  if (regla == null) return true;

  // Planes nombrados explícitamente: es la única forma de que entre un plan acotado.
  if (regla.incluye != null && regla.incluye.indexOf(plan) >= 0) return true;

  if (regla.minimo == null) return true;

  // Un plan acotado nunca cumple un mínimo de la escalera: no está en ella.
  if (EsPlanAcotado(plan)) return false;

  const Nivel = NIVELES[plan];
  const NivelMinimo = NIVELES[regla.minimo];

  // TODO(entitlements): fail-open deliberado. Si el plan no se reconoce
  // (Desconocido, Pruebas, o el perfil todavía no está en localStorage porque el
  // usuario entró por un deep link), se deja pasar en vez de mandarlo al home.
  // Es el mismo comportamiento efectivo de las guardas que había antes y evita
  // sacar a un usuario legítimo de su pantalla por una condición de carrera.
  // Cuando la autorización por plan viva en el backend (tabla de entitlements),
  // esto puede pasar a fail-closed sin riesgo, porque el 401 sería la verdad y
  // el front sólo estaría evitando el viaje.
  if (Nivel == null || NivelMinimo == null) return true;

  return Nivel >= NivelMinimo;
}

export default {
  PLANES,
  EsPlanAcotado,
  PuedeAccederPlan,
};
